<template>
  <!-- Tip on entering Green Screen: hiding the toolbar is what a clean capture needs, and it is
       only reachable by keyboard. Shown on each entry, on devices with a mouse or trackpad;
       closes by itself after a while. -->
  <transition enter-active-class="transition ease-out duration-200" enter-from-class="opacity-0 -translate-y-1"
              leave-active-class="transition ease-in duration-150" leave-to-class="opacity-0 -translate-y-1">
    <div v-if="visible" class="green-screen-tip fixed right-4 top-[94px] z-[450] flex w-[400px] max-w-[calc(100%-32px)] items-start gap-3 rounded-[12px] p-4"
         role="status" data-testid="green-screen-shortcuts-tip">
      <dynamic-svg class="shrink-0 text-primary" src="/svg/keyboard.svg" width="24" height="24" fill="currentColor" fill-opacity="1" />
      <div class="flex-1 min-w-0">
        <div class="text-[16px] leading-[24px] font-semibold text-text-neutral-extreme">{{ title }}</div>
        <p class="mt-1 text-[15px] leading-[22px] text-text-neutral-medium">
          {{ bodyBefore }} <kbd class="tip-key">{{ ALT_KEY }}</kbd><kbd class="tip-key">B</kbd> {{ bodyAfter }}
        </p>
        <button class="mt-2 text-[15px] leading-[22px] font-medium text-primary hover:underline cursor-pointer" data-testid="green-screen-tip-all-shortcuts" @click="openShortcuts">
          {{ allShortcutsLabel }}
        </button>
      </div>
      <button class="shrink-0 text-primary cursor-pointer" :aria-label="closeLabel" data-testid="green-screen-tip-close" @click="close">
        <XMarkIcon class="w-6 h-6" aria-hidden="true" />
      </button>
    </div>
  </transition>
</template>

<script setup>
import { computed, defineEmits, onBeforeUnmount, onMounted, ref } from 'vue';
import { XMarkIcon } from '@heroicons/vue/24/outline';
import DynamicSvg from '@/components/ui/DynamicSvg.vue';
import { translate } from '@/composables/useCaptionSettings';
import { ALT_KEY, hasFinePointer } from '@/composables/useKeyboardShortcuts';

const emit = defineEmits(['open-shortcuts'])

const AUTO_CLOSE_MS = 12000

const title = computed(() => translate('green_screen_tip_title', 'Hide the toolbar for a clean capture'))
const bodyBefore = computed(() => translate('green_screen_tip_body_before', 'Press'))
const bodyAfter = computed(() => translate('green_screen_tip_body_after', 'to hide or show the toolbar.'))
const allShortcutsLabel = computed(() => translate('green_screen_tip_all_shortcuts', 'All keyboard shortcuts'))
const closeLabel = computed(() => translate('common_close', 'Close'))

const visible = ref(false)
let closeTimer = null

const close = () => {
  visible.value = false
  clearTimeout(closeTimer)
}

const openShortcuts = () => {
  close()
  emit('open-shortcuts')
}

// The room mounts this on every switch into Green Screen, so it shows on each entry.
onMounted(() => {
  if (!hasFinePointer()) return
  visible.value = true
  closeTimer = setTimeout(close, AUTO_CLOSE_MS)
})

onBeforeUnmount(() => clearTimeout(closeTimer))
</script>

<style scoped>
/* The design's translucent blue, laid over the bar color so the chroma color can't show
   through it. */
.green-screen-tip {
  background:
    linear-gradient(var(--color-elements-blue-translucent), var(--color-elements-blue-translucent)),
    var(--color-layer-2);
}

.tip-key {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  margin: 0 1px;
  padding: 0 5px;
  border-radius: 5px;
  background: rgba(240, 246, 255, 0.13);
  color: var(--color-text-neutral-extreme);
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  vertical-align: 1px;
}
</style>
